import compact from 'lodash/compact'
import { useCallback, useMemo } from 'react'

import { getColor } from '@Components/Chips/DiscountChip'
import { IWithMoney, Price, Product } from '@Generated/graphql'
import { getBaseAttribute, getPersonalizationsFromProduct, getPersonalizationTypes } from '@Globals/CMS/ProductHelpers'
import { useCheapestShippingPrice, useFreeShippingThreshold } from '@Globals/Metadata/Hooks'
import { Money } from '@Globals/Money'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

export interface ChipOptions {
  buyXPayForY?: { x: number, y: number }
  clubValue?: string
  enableDiscount: boolean
  freeDelivery: boolean
  isBestSeller?: boolean
  isNew: boolean
  isPopularGift?: boolean
  personalizationTypes?: string[]
  price?: Price
  showChips: boolean
}

export const useChipAssets = (product?: Product): ChipOptions => {
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()
  const enableBuy3 = useSplitTest('buy_three_pay_for_two_v1', 'b')

  return useMemo(() => {
    const { value: freeDeliveryThreshold } = freeShippingThreshold as Money || 0
    const isBestSeller = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isBestSeller') || false
    const clubValue = product?.masterVariant?.price?.clubValue?.format || undefined
    const isBundleOffer = product?.type === 'bundles'
    const isNew = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isNew') || false
    const isPopularGift = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isPopularGift') || false
    const isGiftcard = `${product?.id}` === '26342'
    const price = product?.masterVariant?.price?.discount || product?.masterVariant?.price || ({} as IWithMoney)
    const freeDelivery = !!(freeDeliveryThreshold && price?.value && (freeDeliveryThreshold < price.value))
        || cheapestShippingPrice === null
        || cheapestShippingPrice?.value === 0

    // TODO: Enable this after BW23 th-5438
    const bracletMassDiscount = enableBuy3 // && product?.primaryCategories?.some(a => a.id === '7')
    // const sunglassMassDiscount = product?.primaryCategories?.some(a => a.id === '9')
    const buy3payFor2 = bracletMassDiscount ? { x: 3, y: 2 } : undefined

    const showChips = !!(isNew || (
      (product?.masterVariant?.price?.discount?.percent) || product?.masterVariant?.price?.discount?.code?.context !== '')
        || freeDelivery || isBundleOffer)

    return {
      buyXPayForY: buy3payFor2,
      isBestSeller,
      isBundleOffer,
      clubValue,
      isNew,
      price: product?.masterVariant?.price ?? undefined,
      showChips,
      freeDelivery,
      isPopularGift,
      productType: product?.type || '',
      enableDiscount: !isGiftcard,
      personalizationTypes: product ? getPersonalizationTypes(getPersonalizationsFromProduct(product)) : [],
    }
  }, [freeShippingThreshold, product, cheapestShippingPrice, enableBuy3])
}

export const useChipAssetsV2 = (): any => useCallback((product?: Product) => {
  if (!product) {
    return {
      bottomChips: [],
      topChips: [],
    }
  }
  const isBestSeller = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isBestSeller') || false
  const isNew = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isNew') || false
  const personalizable = product?.masterVariant?.attributes?.find(x => x.key === 'personalizations') || false
  const isPopularGift = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isPopularGift') || false
  const isGiftCard = `${product?.id}` === '26342'
  return {
    bottomChips: compact([
      !isGiftCard && product.masterVariant?.price?.discount && {
        color: getColor(product.masterVariant?.price?.discount?.code?.context || ''),
        translation: [
          'common:discount_chip',
          {
            context: product.masterVariant?.price?.discount.code?.context || '',
            discountPercentage: product.masterVariant?.price?.discount?.percent || 0,
          },
        ],
      },
      isNew && !isBestSeller && {
        color: 'blue',
        translation: ['common:BADGE_NEW_LABEL'],
      },
      isBestSeller && !isNew && {
        color: 'blue',
        translation: ['common:WB_BESTSELLERS_LABEL', { count: 1 }],
      },

    ]),
    topChips: compact([
      isPopularGift && {
        color: 'blue',
        translation: ['common:POPULAR_GIFT_TAG'],
      },
      personalizable && {
        color: 'grey',
        translation: ['common:PERSONALISABLE_PRODUCT_TAG'],
      },
    ]),
  }
}, [])
